import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, AlertTriangle, ArrowLeft } from "lucide-react";

/**
 * Public Terms & Conditions page.
 *
 * Kept as a long-form static page rather than a dialog so users can
 * link to it directly (footer, email, etc.) and bookmark sections. The
 * "Disclaimer of Liability" wording is the same pattern used across our
 * sister product (Exhibition Tracker) — see screenshot uploaded
 * 2026-06-18 for the canonical version.
 */
export default function Terms() {
  const lastUpdated = "16 February 2026";
  return (
    <div className="min-h-screen" style={{ background: "#0a0a0a", color: "#e5e5e5" }}>
      <div className="max-w-3xl mx-auto px-6 py-12" data-testid="terms-page">
        <Link
          to="/login"
          className="inline-flex items-center gap-1 text-sm muted-text hover:text-amber-400 mb-8"
          data-testid="terms-back-link"
        >
          <ArrowLeft size={14} /> Back to sign in
        </Link>

        <div className="ccs-label mb-3">Legal</div>
        <h1 className="text-4xl font-semibold tracking-tight mb-2 flex items-center gap-3">
          <ShieldCheck className="text-amber-400" size={32} /> Terms &amp; Conditions
        </h1>
        <div className="muted-text text-sm mb-10">
          Last updated: <span className="mono">{lastUpdated}</span>
        </div>

        <Section n="1" title="About Camera Club Score">
          Camera Club Score is a multi-tenant web application for camera
          clubs to manage their internal competition results — photographer
          rosters, season schedules, individual competition entries, judge
          scores, placements, and end-of-season statistics. The service is
          designed for the day-to-day administration of a single club&rsquo;s
          competition program.
          <br /><br />
          Camera Club Score is <b>not affiliated</b> with the Photographic
          Alliance of Great Britain (PAGB), the Royal Photographic Society
          (RPS), the Federation of Photographic Societies, or any other
          national or international photographic body. Scores and placements
          recorded in this service have <b>no official standing</b> with such
          bodies and exist purely for the convenience of individual clubs.
        </Section>

        <Section n="2" title="Your account &amp; eligibility">
          You must be at least 16 years old to create an account. Each
          registration creates an isolated workspace (&ldquo;club&rdquo;) for
          which the registering user becomes the administrator. Administrators
          may invite additional members (Secretary / Viewer / Member roles) to
          their club.
          <br /><br />
          You are responsible for keeping your password confidential and for
          all activity that occurs under your account. We require email
          verification on registration and reasonable password length; we do
          not provide multi-factor authentication at this time.
        </Section>

        <Section n="3" title="Disclaimer of Liability" highlight>
          This program is provided &ldquo;as is&rdquo; without warranty of any
          kind. Camera Club Score is not responsible for any loss, corruption,
          or unavailability of data stored within the application. You should
          always maintain a hard copy or independent backup of all your
          competition records, scores, placements, and related data. By using
          this service, you acknowledge that you are solely responsible for
          maintaining backup copies of your data.
          <br /><br />
          A built-in <Link to="/login" className="text-amber-400 hover:text-amber-300">Backup</Link> feature
          is provided under <span className="mono">Settings → Backup</span>{" "}
          which lets administrators download a complete copy of their club
          data as a single file. <b>You are strongly encouraged to use it
          regularly</b> and store the file in a location outside of this
          service (your laptop, a Dropbox / Google Drive folder, or any other
          off-platform backup of your choice).
        </Section>

        <Section n="4" title="Your data">
          We store only the data you enter into the service — photographer
          names &amp; codes, competition titles &amp; dates, image titles,
          judge scores, and your team&rsquo;s email addresses. We do
          <b> not</b> store, host, or process the photographic images
          themselves.
          <br /><br />
          Data is held on servers in the European Union and is processed in
          accordance with the UK Data Protection Act 2018 and the UK GDPR.
          You may export all of your data at any time using the Backup
          feature, or request deletion of your club&rsquo;s data by contacting{" "}
          <a className="text-amber-400 hover:text-amber-300" href="mailto:mail@cameraclubscore.com">mail@cameraclubscore.com</a>.
        </Section>

        <Section n="5" title="Acceptable use">
          You agree not to use the service to:
          <ul className="list-disc list-inside mt-2 muted-text space-y-1">
            <li>upload image titles, photographer names or other data that is
              illegal, harassing, defamatory, or infringes the rights of
              others;</li>
            <li>attempt to gain unauthorised access to another club&rsquo;s
              workspace or to the platform&rsquo;s administrative tooling;</li>
            <li>resell, redistribute, or sublicense access to the service
              without our prior written consent.</li>
          </ul>
          Administrators are responsible for the conduct of users they invite
          into their club workspace.
        </Section>

        <Section n="6" title="Service availability">
          We aim to keep the service running but do not guarantee uninterrupted
          access. Planned maintenance, third-party outages (email delivery,
          hosting, DNS), and unforeseen incidents may make the service
          temporarily unavailable. This further reinforces section 3 above:
          please keep your own backups.
        </Section>

        <Section n="7" title="Changes to these terms">
          We may update these Terms &amp; Conditions from time to time. Where
          changes are material we&rsquo;ll prompt for re-acceptance at sign-in.
          Trivial changes (typos, contact details) may be made without notice.
          The &ldquo;Last updated&rdquo; date above always reflects the current
          revision.
        </Section>

        <Section n="8" title="Contact">
          Questions about these terms or your account? Email{" "}
          <a className="text-amber-400 hover:text-amber-300" href="mailto:mail@cameraclubscore.com">mail@cameraclubscore.com</a>.
        </Section>

        <div className="muted-text text-xs mt-12 pt-6" style={{ borderTop: "1px solid #1f1f1f" }}>
          By creating an account you confirm that you have read and agree to
          these Terms &amp; Conditions, including the Disclaimer of Liability
          in section 3.
        </div>
      </div>
    </div>
  );
}


function Section({ n, title, children, highlight = false }) {
  return (
    <section className="mb-10" data-testid={`terms-section-${n}`}>
      <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
        <span className="muted-text mono text-base">{n}.</span> {title}
        {highlight && <AlertTriangle size={16} className="text-amber-400" />}
      </h2>
      <div
        className={"text-sm leading-relaxed " + (highlight ? "p-4" : "")}
        style={highlight ? { background: "#1d160a", border: "1px solid #3a2a10", color: "#e7d4a8" } : {}}
      >
        {children}
      </div>
    </section>
  );
}
